/**
 * 评测器（evaluator）英文 name → 中文展示名映射（**运行时从后端拉取**）。
 *
 * 单一数据源在后端 `utils.evaluator_names.EVALUATOR_NAME_CN`，前端经
 * `GET /evaluation/evaluator/name-map` 拉取并缓存；新增评测器只改后端一处。
 *
 * 展示函数（`getEvaluatorLabel` / `getEvaluatorDisplay`）保持**同步**，读模块缓存，
 * 映射未就绪或拉取失败时回退展示原始英文 name。组件用 `useEvaluatorNames()` 订阅，
 * 映射加载完成后自动触发重渲染。
 */
import { useEffect, useState } from 'react';

import { getEvaluatorDocMap, getEvaluatorNameMap } from '@/apis/simulation';
import { canonicalEvaluatorName } from '@/constants/evaluatorMetrics';

let evaluatorNameCn: Record<string, string> = {};
let loadPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((cb) => cb());
};

/** 拉取并缓存评测器中文名映射（幂等：并发/重复调用只发一次请求）。 */
export const loadEvaluatorNameMap = (): Promise<void> => {
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = getEvaluatorNameMap()
    .then((map) => {
      evaluatorNameCn = map || {};
      notify();
    })
    .catch((e) => {
      // 拉取失败不阻断页面：保持空映射、回退英文展示；打印告警不静默吞掉。
      console.warn('加载评测器中文名映射失败，回退英文展示', e);
      loadPromise = null; // 置空以允许后续重试
    });
  return loadPromise;
};

/**
 * 是否为「评测器」名（仅 ``_evaluator`` 结尾）。
 * 评测器矩阵 / 明细只统计评测器，用它过滤掉 meta_info_computer 等内部统计项，
 * 以及 ``_check`` 检查项（type=3 独立成类，走单独的「检查项」筛选）。
 */
export const isEvaluatorName = (name: string | null | undefined): boolean =>
  (name ?? '').endsWith('_evaluator');

/** 取评测器中文展示名；未登记/未就绪则回退原始英文 name。 */
export const getEvaluatorLabel = (name: string | null | undefined): string => {
  if (!name) return '-';
  return evaluatorNameCn[name] ?? name;
};

/**
 * 展示名：`中文(英文)`；无中文映射时只返回英文 name（避免 `name(name)`）。
 * 用于矩阵/筛选/明细等需要同时呈现中英文的展示位。
 */
export const getEvaluatorDisplay = (name: string | null | undefined): string => {
  if (!name) return '-';
  const cn = evaluatorNameCn[name];
  return cn ? `${cn}(${name})` : name;
};

/**
 * 组件内调用一次：确保映射已发起加载，并在加载完成后触发本组件重渲染，
 * 让同步的 `getEvaluatorLabel` / `getEvaluatorDisplay` 拿到中文名。
 */
export const useEvaluatorNames = (): void => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const cb = () => setVersion((v) => v + 1);
    listeners.add(cb);
    void loadEvaluatorNameMap();
    void loadEvaluatorDocMap();
    return () => {
      listeners.delete(cb);
    };
  }, []);
};

let evaluatorDocUrl: Record<string, string> = {};
let docLoadPromise: Promise<void> | null = null;

/** 拉取并缓存评测器飞书文档链接（后台白名单「飞书文档链接」列）；失败不阻断，重试留给下次调用。 */
export const loadEvaluatorDocMap = (): Promise<void> => {
  if (docLoadPromise) return docLoadPromise;
  docLoadPromise = getEvaluatorDocMap()
    .then((map) => {
      evaluatorDocUrl = map || {};
      notify();
    })
    .catch((e) => {
      console.warn('加载评测器飞书文档链接失败，不显示文档入口', e);
      docLoadPromise = null;
    });
  return docLoadPromise;
};

/** 取评测器文档链接；按 canonical snake_case 匹配（兼容 CamelCase 上报名）。未配置返回 undefined。 */
export const getEvaluatorDocUrl = (name: string | null | undefined): string | undefined => {
  if (!name) return undefined;
  return evaluatorDocUrl[name] ?? evaluatorDocUrl[canonicalEvaluatorName(name)];
};
